import { CarAxleType, TireLoadIndex, TireSpeedIndex } from '@navio/nsdk';
import { JsonParam, type QueryParamConfig } from 'use-query-params';
import { z } from 'zod';

const fitmentSchema = z.object({
  axle: z.enum(CarAxleType),
  dimension: z.string().min(1).optional(),
  loadIndexGte: z.enum(TireLoadIndex).optional(),
  speedIndexGte: z.enum(TireSpeedIndex).optional()
});

export type TireAxleFitment = z.input<typeof fitmentSchema>;

export const hasTireFitmentConstraints = (fitment: TireAxleFitment) =>
  Boolean(fitment.dimension || fitment.loadIndexGte !== undefined || fitment.speedIndexGte !== undefined);

export const tireAxleFitmentKey = (fitment: TireAxleFitment): string =>
  JSON.stringify([fitment.axle, fitment.dimension, fitment.loadIndexGte, fitment.speedIndexGte]);

export const normalizeTireAxleFitments = (fitments: TireAxleFitment[]): TireAxleFitment[] => [
  ...new Map(
    fitments.filter(hasTireFitmentConstraints).map((fitment) => [tireAxleFitmentKey(fitment), fitment])
  ).values()
];

export const tireAxleFitmentsQuery = (
  fitments: TireAxleFitment[],
  axle?: CarAxleType | 'any' | null
): string | undefined =>
  fitments.length === 0
    ? undefined
    : JSON.stringify(
        fitments
          .filter((fitment) => !axle || axle === 'any' || fitment.axle === axle)
          .map(({ dimension, loadIndexGte, speedIndexGte }) => ({ dimension, loadIndexGte, speedIndexGte }))
      );

const fitmentsSchema = z.array(fitmentSchema.refine(hasTireFitmentConstraints)).max(100);
export const tireAxleFitmentsParam: QueryParamConfig<TireAxleFitment[]> = {
  encode: (value) => JsonParam.encode(normalizeTireAxleFitments(value)),
  decode: (value) => {
    const parsed = fitmentsSchema.safeParse(JsonParam.decode(value));
    return parsed.success ? normalizeTireAxleFitments(parsed.data) : [];
  }
};
